import { saveCustomer } from 'actions';
import Permissions from 'components/Permissions';
import ToggleButton from 'components/ToggleButton';
import useClickOutside from 'components/utils/useClickOutside';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { DayPicker, getDefaultClassNames, useDayPicker } from 'react-day-picker';
import 'react-day-picker/src/style.css';
import { usePopper } from 'react-popper';

import { useDispatch, useSelector } from 'react-redux';
import { getCustomer, getUser } from 'selectors/auth';
import { isValidDate } from 'utils/dateValidation';
import { isActionAuthorized } from 'utils/permissions';

const CustomNextMonthButton: React.FC<{ single?: boolean }> = (props) => {
  const { single = false } = props;
  const dayPickerContext = useDayPicker();
  const nextMonth = moment(dayPickerContext.nextMonth).endOf('month');
  const current = moment();

  const disabled = single
    ? nextMonth.subtract(1, 'month').isAfter(current)
    : nextMonth.isAfter(current);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <button {...props} type="button" disabled={disabled}>
      <svg className="rdp-chevron" width="24" height="24" viewBox="0 0 24 24">
        <polygon points="8 18.612 14.1888889 12.5 8 6.37733333 9.91111111 4.5 18 12.5 9.91111111 20.5" />
      </svg>
    </button>
  );
};

const CustomerData: React.FC = () => {
  const dispatch = useDispatch();
  const customer = useSelector(getCustomer);
  const user = useSelector(getUser);

  const [storeCommunications, setStoreCommunications] = useState(
    customer.config.store_communications
  );
  const [storeDocument, setStoreDocument] = useState(customer.config.store_communications);
  const [allowUserProfiles, setAllowUsersProfiles] = useState(customer.config.allow_user_profile);
  const [communcationRetentionDays, setCommuncationRetentionDays] = useState(
    customer.config?.communication_retention_days || 0
  );
  const [syncStartDate, setSyncStartDate] = useState<string | null>(
    customer.config?.sync_start_date || moment.utc().format()
  );

  const ref = useRef<HTMLDivElement>(null);

  const [showCalendar, setShowCalendar] = useState(false);
  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(ref.current, popperRef, {
    placement: 'bottom-start',
  });

  useClickOutside({ current: popperRef }, () => setShowCalendar(false));
  const defaultClassnames = getDefaultClassNames();

  const handleSave = (): void => {
    dispatch(
      saveCustomer({
        config: {
          ...customer.config,
          store_communications: storeCommunications,
          store_document: storeDocument,
          communication_retention_days: communcationRetentionDays,
          sync_start_date: syncStartDate,
          allow_user_profile: allowUserProfiles,
        },
      })
    );
  };

  const handleCancel = (): void => {
    setStoreCommunications(customer.config.store_communications);
    setStoreDocument(customer.config.store_communications);
    setCommuncationRetentionDays(customer.config?.communication_retention_days || 0);
    setSyncStartDate(customer.config?.sync_start_date || moment.utc().format());
    setAllowUsersProfiles(customer.config.allow_user_profile || false);
  };

  const storeMessageBody = (
    <div className="p-4 flex justify-between items-start">
      <div className="max-w-156 flex flex-col gap-1">
        <span className="flex items-center text-body">Store message body</span>
        <p className="text-small">
          Improves performance by allowing LitLingo to store a copy of your messages. If disabled,
          the system will make a new request each time a message is processed or viewed.
        </p>
      </div>

      <ToggleButton
        dataTestid="store-message-body-button"
        isOn={storeDocument}
        disabled={!user.roles.includes('super-admin')}
        handleToggleClick={(): void => {
          setStoreDocument((s) => !s);
        }}
      />
    </div>
  );

  const messageBodyRetention = (
    <div className="p-4 flex justify-between items-center">
      <span className="flex items-center text-body">Message body retention</span>
      {user.roles.includes('super-admin') ? (
        <div className="flex flex-row gap-2 items-center">
          <input
            data-testid="message-body-retention-input"
            className="form-textarea text-body text-center h-6 w-10 px-1"
            id="retention"
            name="retention"
            value={communcationRetentionDays}
            onChange={(e): void =>
              setCommuncationRetentionDays(Number.parseInt(e.target.value, 10))
            }
            type="number"
          />
          <span>Days</span>
        </div>
      ) : (
        <div className="flex flex-row items-center text-body">
          <span>{`${customer.config?.communication_retention_days || 0} Days`}</span>
        </div>
      )}
    </div>
  );

  const messagesSyncedAsOf = (
    <div className="p-4 flex justify-between">
      <span className="flex items-center text-body">Messages synced as of</span>
      <div ref={ref} className="flex flex-row items-center">
        <button
          type="button"
          onClick={(): void => {
            setShowCalendar(true);
          }}
          disabled={!user.roles.includes('super-admin')}
        >
          {syncStartDate != null && isValidDate(syncStartDate)
            ? moment(syncStartDate).format('MM/DD/yyyy')
            : moment().format('mmddyyyy')}
        </button>
        {showCalendar && (
          <div
            ref={(node): void => {
              setPopperRef(node);
            }}
            className="fixed flex flex-col gap-2 items-center min-w-54 px-3 pt-3 pb-2 mt-1 bg-white border border-litlingo-gray-2 rounded z-alert"
            style={{ ...styles.popper, filter: 'drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))' }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...attributes.popper}
            aria-hidden
            onClick={(e): void => e.stopPropagation()}
          >
            <DayPicker
              mode="single"
              captionLayout="dropdown-years"
              startMonth={new Date(2020, 1)}
              defaultMonth={
                syncStartDate != null && isValidDate(syncStartDate)
                  ? new Date(syncStartDate)
                  : new Date()
              }
              selected={
                syncStartDate != null && isValidDate(syncStartDate)
                  ? new Date(syncStartDate)
                  : new Date()
              }
              onSelect={(day: Date | undefined): void => {
                if (day) {
                  const valueDate = moment.utc(day).format();
                  setSyncStartDate(valueDate);
                }
              }}
              numberOfMonths={1}
              components={{
                NextMonthButton: (nextMonthProps) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <CustomNextMonthButton {...nextMonthProps} single />
                ),
              }}
              classNames={{
                day: `${defaultClassnames.day} text-small`,
              }}
              modifiersClassNames={{
                disabled: 'disabled-date',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );

  const userProfile = (
    <div className="p-4 flex justify-between items-start">
      <div className="max-w-156 flex flex-col gap-1">
        <span className="flex items-center text-body">User Profiles</span>
        <p className="text-small">
          Allow for additional insights about the end-users to be seen within the retention window.
        </p>
      </div>

      <ToggleButton
        dataTestid="user-profile-button"
        isOn={allowUserProfiles}
        disabled={!user.roles.includes('super-admin')}
        handleToggleClick={(): void => {
          setAllowUsersProfiles((s) => !s);
        }}
      />
    </div>
  );

  const hasChanges = (): boolean => {
    if (customer.config.store_communications !== storeCommunications) return true;
    if (customer.config.store_document !== storeDocument) return true;
    if (customer.config.communication_retention_days !== communcationRetentionDays) return true;
    if (customer.config.sync_start_date !== syncStartDate) return true;
    if (customer.config.allow_user_profile !== allowUserProfiles) return true;

    return false;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-2 justify-between items-center">
        <div className="flex flex-row gap-2 items-base">
          <p className="text-menu-item">Data</p>
          <p className="text-small">Contact LitLingo Support to make changes</p>
        </div>
        {isActionAuthorized}
        <Permissions action="customers.advancedOptions">
          <div className="flex flex-row gap-2">
            <button
              type="button"
              className="button button--secondary flex justify-center w-20 h-8"
              onClick={handleCancel}
            >
              <span className="font-bold">Cancel</span>
            </button>
            <button
              data-testid="save-button"
              type="button"
              className="button button--primary flex justify-center w-20 h-8"
              onClick={handleSave}
              disabled={!hasChanges()}
            >
              <span>Save</span>
            </button>
          </div>
        </Permissions>
      </div>
      <div className="flex flex-col bg-white border border-litlingo-gray-2 rounded">
        <div>{storeMessageBody}</div>
        <div className="border-t border-litlingo-gray-2">{messageBodyRetention}</div>
        <div className="border-t border-litlingo-gray-2">{messagesSyncedAsOf}</div>
        <div className="border-t border-litlingo-gray-2">{userProfile}</div>
      </div>
    </div>
  );
};

export default CustomerData;
